import { BarChart3, FileText, Mail, Settings, Shield, UsersRound } from 'lucide-react'
import { AppPaths } from '@/config/app-paths'
import type { StaffNavContext } from '../staff-nav-context'
import { group, leaf, type StaffNavGroup, type StaffNavNode } from '../nav-node'

/**
 * Platform / system settings — not day-to-day module work.
 * Label is "Settings" so it is not confused with Payroll Console or general admin work.
 * Group id stays `admin` for dormant navOverrides compatibility.
 */
export function buildAdminGroup(ctx: StaffNavContext): StaffNavGroup | null {
  const children: StaffNavNode[] = []

  if (ctx.showTeam) {
    children.push(
      leaf(
        'admin_users',
        'Users',
        AppPaths.adminTeam,
        UsersRound,
        ['team', 'users', 'roles', 'staff', 'access'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showAccessControl) {
    children.push(
      leaf(
        'access_control',
        'Access Control',
        AppPaths.adminAccessControl,
        Shield,
        ['roles', 'permissions', 'capabilities', 'features', 'access control'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showAdminSearch) {
    children.push(
      leaf('analytics', 'Analytics', AppPaths.adminAnalytics, BarChart3, [
        'dashboard',
        'stats',
      ]),
    )
  }

  // Manage Clients lives under Sales when that group is on; otherwise Settings.
  if (ctx.showClientIdentityNav && !ctx.showSalesNav) {
    children.push(
      leaf('manage_clients', 'Manage Clients', AppPaths.adminClients, UsersRound, [
        'client',
        'company',
      ]),
    )
  }

  if (ctx.showAdminContracts && !ctx.showSalesNav) {
    children.push(
      leaf(
        'admin_contracts',
        'Contracts',
        AppPaths.adminContracts,
        FileText,
        ['client', 'commercial', 'contract', 'renewal'],
        { activeWhenPrefix: true },
      ),
    )
  }

  if (ctx.showStaffOperations) {
    children.push(
      leaf(
        'staff_operations',
        'Staff Operations',
        AppPaths.adminStaffOperations,
        BarChart3,
        ['operations', 'insights'],
      ),
    )
  }

  if (ctx.showNotificationRulesNav) {
    children.push(
      leaf(
        'notification_rules',
        'Notification Rules',
        AppPaths.adminNotificationRules,
        Settings,
        ['notification', 'alerts', 'rules'],
      ),
    )
  }

  if (ctx.showContactInquiries) {
    children.push(
      leaf(
        'contact_inquiries',
        'Contact Inquiries',
        AppPaths.adminContactInquiries,
        Mail,
        ['contact', 'inquiry', 'website'],
      ),
    )
  }

  if (ctx.showDocumentFilingAdmin) {
    children.push(
      leaf(
        'document_filing_assign',
        'Assign documents',
        AppPaths.documentFilingAssign,
        FileText,
        ['documents', 'filing', 'assign'],
      ),
      leaf(
        'document_filing_review',
        'Review documents',
        AppPaths.documentFilingReview,
        FileText,
        ['documents', 'filing', 'review'],
        { activeWhenPrefix: true },
      ),
    )
  }

  return group('admin', 'Settings', Settings, ['admin', 'settings', 'platform'], children)
}
